import { useState } from 'react';
import { 
  Search,
  Filter,
  AlertTriangle,
  ArrowUp,
  Clock,
  User,
  Star,
  ChevronRight,
  CheckCircle,
  XCircle,
  ArrowUpRight,
  MoreVertical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Agent, Escalation, EscalationStatus } from '@/types/supervisor';

const escalations: Escalation[] = [];
const agents: Agent[] = [];
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

export function EscalationQueue() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedEscalation, setSelectedEscalation] = useState<Escalation | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'assign' | 'resolve' | 'priority'>('assign');
  const [actionNotes, setActionNotes] = useState('');
  const { toast } = useToast();

  const filteredEscalations = escalations
    .filter(esc => {
      const matchesSearch = esc.ticketTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        esc.ticketId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        esc.customerName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || esc.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      // Sort by: pending first, then by VIP, then by SLA risk
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;
      if (a.isVip && !b.isVip) return -1;
      if (!a.isVip && b.isVip) return 1;
      if (a.slaAtRisk && !b.slaAtRisk) return -1;
      if (!a.slaAtRisk && b.slaAtRisk) return 1;
      return new Date(b.escalatedAt).getTime() - new Date(a.escalatedAt).getTime();
    });

  const pendingCount = escalations.filter(e => e.status === 'pending').length;
  const inProgressCount = escalations.filter(e => e.status === 'in_progress').length;

  const getReasonIcon = (reason: string) => {
    switch (reason) {
      case 'vip': return <Star className="h-3 w-3" />;
      case 'sla_risk': return <Clock className="h-3 w-3" />;
      case 'complexity': return <AlertTriangle className="h-3 w-3" />;
      case 'technical': return <ArrowUp className="h-3 w-3" />;
      default: return <User className="h-3 w-3" />;
    }
  };

  const getStatusBadge = (status: EscalationStatus) => {
    const styles = {
      pending: "bg-warning/20 text-warning border-warning",
      in_progress: "bg-primary/20 text-primary border-primary",
      resolved: "bg-success/20 text-success border-success",
      de_escalated: "bg-muted text-muted-foreground border-muted",
    };
    return styles[status];
  };

  const handleAction = (escalation: Escalation, action: 'assign' | 'resolve' | 'priority') => {
    setSelectedEscalation(escalation);
    setActionType(action);
    setActionNotes('');
    setActionDialogOpen(true);
  };

  const submitAction = () => {
    toast({
      title: actionType === 'assign' ? "Escalation Assigned" : actionType === 'resolve' ? "Escalation Resolved" : "Priority Updated",
      description: `${selectedEscalation?.ticketId} has been updated.`,
    });
    setActionDialogOpen(false);
    setSelectedEscalation(null);
  };

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4 animate-fade-in sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5 sm:space-y-1">
          <h1 className="text-xl font-bold flex items-center gap-2 sm:text-2xl">
            <AlertTriangle className="h-5 w-5 text-warning sm:h-6 sm:w-6" />
            Escalation Queue
          </h1>
          <p className="text-sm text-muted-foreground sm:text-base">Manage escalated tickets requiring supervisor attention</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="destructive" className="text-xs">
            {pendingCount} pending
          </Badge>
          <Badge variant="outline" className="text-xs border-primary text-primary">
            {inProgressCount} in progress
          </Badge>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-2">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search escalations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="de_escalated">De-escalated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Escalation List */}
      <div className="space-y-3">
        {filteredEscalations.map((escalation) => (
          <Card 
            key={escalation.id} 
            className={cn(
              "border-2 transition-all hover:border-primary/50",
              escalation.status === 'pending' && escalation.slaAtRisk && "border-l-4 border-l-destructive",
              escalation.status === 'pending' && escalation.isVip && !escalation.slaAtRisk && "border-l-4 border-l-warning"
            )}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {/* Left: Main Info */}
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-muted-foreground">{escalation.ticketId}</span>
                    <Badge variant="outline" className={getStatusBadge(escalation.status)}>
                      {escalation.status.replace('_', ' ')}
                    </Badge>
                    {escalation.isVip && (
                      <Badge className="bg-warning/20 text-warning-foreground text-xs">
                        <Star className="h-3 w-3 mr-1" />
                        VIP
                      </Badge>
                    )}
                    {escalation.slaAtRisk && (
                      <Badge variant="destructive" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        SLA Risk
                      </Badge>
                    )}
                    {escalation.priorityAfter && escalation.priorityAfter !== escalation.priorityBefore && (
                      <Badge variant="outline" className="text-xs border-primary text-primary">
                        <ArrowUp className="h-3 w-3 mr-1" />
                        {escalation.priorityBefore} → {escalation.priorityAfter}
                      </Badge>
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-sm truncate">{escalation.ticketTitle}</h3>
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {escalation.customerName}
                    </span>
                    <span>•</span>
                    <span>{escalation.department}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      {getReasonIcon(escalation.reason)}
                      {escalation.reason.replace('_', ' ')}
                    </span>
                  </div>

                  {escalation.description && (
                    <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                      {escalation.description}
                    </p>
                  )}

                  <div className="text-xs text-muted-foreground">
                    Escalated by <span className="font-medium">{escalation.escalatedBy}</span>
                    {' • '}
                    {new Date(escalation.escalatedAt).toLocaleString()}
                    {escalation.assignedSupervisor && (
                      <>
                        {' • '}
                        Assigned to <span className="font-medium text-primary">{escalation.assignedSupervisor}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Right: Actions */}
                <div className="flex flex-col gap-2">
                  {escalation.status === 'pending' && (
                    <>
                      <Button 
                        size="sm" 
                        onClick={() => handleAction(escalation, 'assign')}
                        className="text-xs"
                      >
                        <User className="h-3 w-3 mr-1" />
                        Take Over
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleAction(escalation, 'priority')}
                        className="text-xs"
                      >
                        <ArrowUp className="h-3 w-3 mr-1" />
                        Bump Priority
                      </Button>
                    </>
                  )}
                  {escalation.status === 'in_progress' && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-xs border-success text-success hover:bg-success/10"
                      onClick={() => handleAction(escalation, 'resolve')}
                    >
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Resolve
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-xs">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        <ArrowUpRight className="h-4 w-4 mr-2" />
                        View Ticket
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <User className="h-4 w-4 mr-2" />
                        Reassign to Agent
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive">
                        <XCircle className="h-4 w-4 mr-2" />
                        De-escalate
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'assign' && 'Take Over Escalation'}
              {actionType === 'resolve' && 'Resolve Escalation'}
              {actionType === 'priority' && 'Update Priority'}
            </DialogTitle>
            <DialogDescription>
              {selectedEscalation?.ticketId} - {selectedEscalation?.ticketTitle}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {actionType === 'assign' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Assign to Agent (optional)</label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Keep assigned to me" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="me">Keep assigned to me</SelectItem>
                    {agents.filter(a => a.status !== 'offline').map(agent => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name} ({agent.currentLoad}/{agent.maxCapacity})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {actionType === 'priority' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">New Priority</label>
                <Select defaultValue={selectedEscalation?.priorityBefore}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="P1">P1 - Critical</SelectItem>
                    <SelectItem value="P2">P2 - High</SelectItem>
                    <SelectItem value="P3">P3 - Medium</SelectItem>
                    <SelectItem value="P4">P4 - Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                placeholder="Add notes about this action..."
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitAction}>
              {actionType === 'assign' && 'Take Over'}
              {actionType === 'resolve' && 'Resolve'}
              {actionType === 'priority' && 'Update Priority'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
